/**
 * Enhanced DataLayer events for GA4 + PostHog funnels.
 *
 *  - Scroll depth: fires page_scroll with { depth: 25|50|75|100 }
 *  - Outbound click: fires outbound_click with { destination_url, destination_host }
 *  - Form field focus/blur: fires form_field_focus / form_field_blur with { form_id, field_name }
 *
 * Every event also flows through PostHog (dataLayer auto-enriched with
 * attribution by src/lib/analytics.ts).
 */
'use client';

import { useEffect } from 'react';
import { pushEvent } from '@/lib/analytics/core';

const FIRED_SCROLL = new Set<number>();

function handleScroll(): void {
  const doc = document.documentElement;
  const scrolled = doc.scrollTop || document.body.scrollTop;
  const height = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
  if (height <= 0) return;
  const pct = Math.round((scrolled / height) * 100);
  for (const mark of [25, 50, 75, 100]) {
    if (pct >= mark && !FIRED_SCROLL.has(mark)) {
      FIRED_SCROLL.add(mark);
      pushEvent('page_scroll', { depth: mark, page_path: location.pathname });
    }
  }
}

function handleOutboundClick(e: MouseEvent): void {
  // e.target may not be an Element (document/text node) — guard before .closest
  const el = e.target instanceof Element ? e.target : null;
  const target = el?.closest('a[href]') as HTMLAnchorElement | null;
  if (!target) return;
  try {
    const url = new URL(target.href, window.location.origin);
    if (url.origin === window.location.origin) return;
    pushEvent('outbound_click', {
      destination_url: url.toString(),
      destination_host: url.hostname,
      link_text: target.textContent?.trim().slice(0, 120) ?? null,
    });
  } catch { /* not a valid URL */ }
}

function handleFormFocusIn(e: FocusEvent): void {
  const el = e.target as HTMLElement | null;
  if (!el || !('name' in el)) return;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;
  const form = (el as HTMLInputElement).form;
  const formId = form?.id || form?.getAttribute('data-form') || location.pathname;
  const fieldName = (el as HTMLInputElement).name || el.id || (el as HTMLInputElement).type || 'unknown';
  pushEvent('form_field_focus', { form_id: formId, field_name: fieldName });
}

function handleFormFocusOut(e: FocusEvent): void {
  const el = e.target as HTMLElement | null;
  if (!el) return;
  const tag = el.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;
  const form = (el as HTMLInputElement).form;
  const formId = form?.id || form?.getAttribute('data-form') || location.pathname;
  const fieldName = (el as HTMLInputElement).name || el.id || (el as HTMLInputElement).type || 'unknown';
  const value = (el as HTMLInputElement).value;
  pushEvent('form_field_blur', {
    form_id: formId,
    field_name: fieldName,
    value_length: typeof value === 'string' ? value.length : 0,
  });
}

// ---------------------------------------------------------------------------
// AEO page-level events (Initiative 4 measurement layer)
// ---------------------------------------------------------------------------
//
// Fire-and-forget helpers callable from new pillar/category/vs pages so we can
// build PostHog funnels: aeo_page_view -> aeo_faq_expand -> aeo_primary_cta_click.
// All events route through pushEvent() -> dataLayer -> PostHog with the
// standard attribution enrichment (ft_*, lt_*).

export type AeoPageFamily = 'pillar' | 'vs' | 'category';
export type AeoTargetTerm =
  | 'ai-voice'
  | 'ai-receptionist'
  | 'ai-business-automation'
  | 'ai-answering-service'
  | 'ai-discovery';
export type AeoCtaLocation = 'hero' | 'mid' | 'footer';

export function trackAeoPageView(params: {
  page_family: AeoPageFamily;
  target_term: AeoTargetTerm;
}): void {
  pushEvent('aeo_page_view', {
    page_family: params.page_family,
    target_term: params.target_term,
    page_path: typeof location !== 'undefined' ? location.pathname : null,
  });
}

export function trackAeoFaqExpand(params: {
  question: string;
  position: number;
  page_family: AeoPageFamily;
}): void {
  pushEvent('aeo_faq_expand', {
    question: params.question.slice(0, 200),
    position: params.position,
    page_family: params.page_family,
    page_path: typeof location !== 'undefined' ? location.pathname : null,
  });
}

export function trackAeoCitableStatView(params: {
  claim: string;
  page_family: AeoPageFamily;
}): void {
  pushEvent('aeo_citable_stat_view', {
    claim: params.claim.slice(0, 200),
    page_family: params.page_family,
    page_path: typeof location !== 'undefined' ? location.pathname : null,
  });
}

export function trackAeoHubLinkClick(params: {
  source_page: string;
  dest_page: string;
  anchor_text: string;
}): void {
  pushEvent('aeo_hub_link_click', {
    source_page: params.source_page,
    dest_page: params.dest_page,
    anchor_text: params.anchor_text.slice(0, 120),
  });
}

export function trackAeoPrimaryCtaClick(params: {
  page_family: AeoPageFamily;
  cta_location: AeoCtaLocation;
}): void {
  pushEvent('aeo_primary_cta_click', {
    page_family: params.page_family,
    cta_location: params.cta_location,
    page_path: typeof location !== 'undefined' ? location.pathname : null,
  });
}

export function registerEnhancedEvents(): () => void {
  const onScroll = (): void => {
    if (typeof requestIdleCallback !== 'undefined') requestIdleCallback(handleScroll);
    else handleScroll();
  };
  // Reset per page navigation (AnalyticsProvider re-mounts with route change in most SPAs;
  // for App Router we clear on URL change instead).
  const resetOnNav = (): void => FIRED_SCROLL.clear();

  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('click', handleOutboundClick, true);
  document.addEventListener('focusin', handleFormFocusIn, true);
  document.addEventListener('focusout', handleFormFocusOut, true);
  window.addEventListener('popstate', resetOnNav);

  return (): void => {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('click', handleOutboundClick, true);
    document.removeEventListener('focusin', handleFormFocusIn, true);
    document.removeEventListener('focusout', handleFormFocusOut, true);
    window.removeEventListener('popstate', resetOnNav);
  };
}

/**
 * Drop into the root layout to enable scroll-depth / outbound / form
 * events across the entire site. No-op on server.
 */
export function useEnhancedEvents(): void {
  useEffect(() => {
    return registerEnhancedEvents();
  }, []);
}
